<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import { blownInsulation, drywall, screed } from '@/actions/App/Http/Controllers/ServiceController'

const posts = [
    {
        id: 1,
        title: 'Foukaná izolace',
        href: blownInsulation.url(),
        imageUrl: '/src/services/montaze-sadrokartonu-ukazka.webp',
    },
    {
        id: 2,
        title: 'Betonové stěrky',
        href: screed.url(),
        imageUrl: '/src/services/betonova-sterka-ukazka.webp',
    },
]
</script>

<template>
    <AppLayout
        meta_title="Montáž sádrokartonu Olomouc a okolí"
        meta_description="Provádíme montáže sádrokartonu v Olomouci a po celé Moravě. Příčky, podhledy, půdní vestavby. Kvalitní práce, rychlé dodání."
        :meta_canonical="drywall.url()"
    >
        <template #content>
            <section class="section-width mt-16">
                <header class="max-w-4xl">
                    <h1 class="header-h">Montáže sádrokartonu</h1>
                    <p class="mt-6 text-xl/8 text-balance text-gray-700">
                        Provádíme montáže sádrokartonu v Olomouci a po celé Moravě. Příčky, podhledy, půdní vestavby.
                        Kvalitní práce, rychlé dodání.
                    </p>
                </header>

                <section
                    class="relative -top-32 mt-20 grid grid-cols-1 overflow-hidden pt-32 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-8"
                >
                    <div class="lg:pr-8">
                        <h2 class="text-2xl font-semibold tracking-tight text-pretty text-gray-900">Jak to probíhá</h2>
                        <p class="mt-6 text-base/7 text-gray-600">
                            Montáž sádrokartonu je dnes jedním z nejrychlejších a nejoblíbenějších způsobů, jak upravit
                            interiér – ať už jde o stavbu příček, podhledů, nebo obklad stěn. Celý proces se skládá z
                            několika kroků:
                        </p>

                        <div class="mt-8 space-y-6">
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">1. Příprava</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Změření prostoru a zakreslení, kde budou stát příčky nebo podhledy.</li>
                                    <li>Vyčištění a vyrovnání podkladu.</li>
                                    <li>Příprava materiálu (sádrokartonové desky, profily, šrouby, tmel, pásky).</li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">2. Konstrukce z profilů</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Na podlahu a strop se upevní vodicí profily.</li>
                                    <li>
                                        Do nich se vkládají svislé stojiny (u příček) nebo závěsy a nosné profily (u
                                        podhledů).
                                    </li>
                                    <li>Vznikne kovová kostra, která nese samotné desky.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">3. Montáž desek</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Sádrokartonové desky se řežou na požadovaný rozměr.</li>
                                    <li>Pomocí samořezných šroubů se připevňují k ocelovým profilům.</li>
                                    <li>Desky se pokládají s posunem spár, aby byla konstrukce pevnější.</li>
                                    <li>
                                        Pokud je sádrokarton použit v koupelně či kuchyni, používají se speciální
                                        impregnované desky.
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">4. Instalace izolace (volitelné)</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>
                                        Do dutin konstrukce se často vkládá minerální vata – pro tepelnou i akustickou
                                        izolaci.
                                    </li>
                                    <li>
                                        U příček je to prakticky standard, u podhledů se izolace používá podle potřeby.
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">5. Spárování a tmelení</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Spoje mezi deskami se přetmelí a vyztuží výztužnou páskou.</li>
                                    <li>Tmelí se také hlavičky šroubů.</li>
                                    <li>Po zaschnutí se povrch přebrousí do hladka.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">6. Finální úpravy</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Hotový povrch lze natřít barvou, vytapetovat nebo jinak dekorovat.</li>
                                </ul>
                            </div>

                            <div class="border-brand-orange mt-8 border-l-2 bg-orange-50 p-4">
                                <p class="text-sm/6 text-orange-900">
                                    <span class="font-semibold">Výhoda sádrokartonu:</span> rychlá montáž, nízká
                                    hmotnost a možnost snadných oprav. Zároveň dovoluje skrýt instalace (elektřinu,
                                    rozvody) do dutin konstrukce.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        class="mt-4 flex flex-col justify-center gap-8 sm:justify-end sm:pl-20 md:flex-row lg:-mt-40 lg:pl-0"
                    >
                        <div
                            class="mx-auto w-72 flex-none space-y-8 pt-32 sm:ml-0 sm:pt-80 lg:w-50 lg:pt-36 xl:w-72 xl:pt-80"
                        >
                            <div class="relative">
                                <img
                                    alt="Montáže sádrokartonu Olomouc a okolí"
                                    src="/src/services/sadrokartony_1.webp"
                                    class="aspect-square w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                />
                                <div
                                    class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-gray-900/10 ring-inset"
                                />
                            </div>
                        </div>
                        <div class="mx-auto w-72 flex-none space-y-8 sm:pt-52 lg:w-50 lg:pt-36 xl:w-72">
                            <div class="relative">
                                <img
                                    alt="Montáže sádrokartonu Olomouc"
                                    src="/src/services/sadrokartony_3.webp"
                                    class="aspect-square w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                />
                                <div
                                    class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-gray-900/10 ring-inset"
                                />
                            </div>
                            <div class="relative">
                                <img
                                    alt="Montáže sádrokartonu v podkroví"
                                    src="/src/services/sadrokartony_2.webp"
                                    class="aspect-square w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                />
                                <div
                                    class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-gray-900/10 ring-inset"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-10 lg:col-span-1 lg:mt-0">
                        <p class="text-base/7 font-semibold text-gray-500">Provádíme</p>
                        <hr class="my-2 border-t border-gray-200" />
                        <ul class="grid list-inside list-disc grid-cols-1 gap-y-4">
                            <li class="text-sm/6 text-gray-600">
                                kompletní výstavba podkroví včetně podlah z fermacellu
                            </li>
                            <li class="text-sm/6 text-gray-600">podhledy a příčky v rodinných domech</li>
                            <li class="text-sm/6 text-gray-600">různé desingové prvky</li>
                            <li class="text-sm/6 text-gray-600">rozdělení velké místnosti na více jednotek příčkou</li>
                            <li class="text-sm/6 text-gray-600">kazetové podhledy</li>
                        </ul>
                    </div>

                    <div class="mt-10 lg:col-span-1 lg:mt-0">
                        <p class="text-base/7 font-semibold text-gray-500">Nástroje a příslušentví</p>
                        <hr class="my-2 border-t border-gray-200" />
                        <ul class="grid list-inside list-disc grid-cols-1 gap-y-4">
                            <li class="text-sm/6 text-gray-600">sádrokartonové desky</li>
                            <li class="text-sm/6 text-gray-600">profilové plechy</li>
                            <li class="text-sm/6 text-gray-600">montážní materiál</li>
                            <li class="text-sm/6 text-gray-600">izolační materiály</li>
                            <li class="text-sm/6 text-gray-600">nástroje pro montáž</li>
                        </ul>
                    </div>

                    <div class="col-span-full mt-10">
                        <div class="mx-auto max-w-2xl text-center">
                            <h2 class="header-h text-center">Další služby</h2>
                        </div>
                        <div
                            class="bg-brand-orange mx-auto mt-5 grid max-w-2xl grid-cols-1 gap-5 rounded-4xl p-4 lg:mx-0 lg:max-w-none lg:grid-cols-2"
                        >
                            <article
                                v-for="post in posts"
                                :key="post.id"
                                class="relative flex flex-col items-start justify-between"
                            >
                                <div class="relative w-full">
                                    <img
                                        :src="post.imageUrl"
                                        alt=""
                                        class="aspect-video w-full rounded-2xl bg-gray-100 object-cover sm:aspect-2/1 lg:aspect-3/2"
                                    />
                                    <div class="absolute inset-0 rounded-2xl inset-ring inset-ring-gray-900/10" />
                                </div>

                                <div class="flex w-full justify-center">
                                    <h3
                                        class="font-chivo mt-3 text-xl font-semibold tracking-tight text-white sm:text-3xl"
                                    >
                                        {{ post.title }}
                                    </h3>
                                </div>

                                <Link
                                    :href="post.href"
                                    class="absolute inset-0"
                                />
                            </article>
                        </div>
                    </div>
                </section>
            </section>
        </template>
    </AppLayout>
</template>
